import { eligibilityApi } from '@api';
import { Button, Input } from '@components';
import { StepperContext } from '@components/Stepper';
import { LocalStorageKey, ZIP_CODE_REGEX } from '@constants';
import { useIsMobile } from '@hooks';
import { Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import NotIncludedInResponsibleForZIPsStep from './NotIncludedInResponsibleForZIPsStep';

interface IZipCheckStepProps {
  onSubmit: () => void;
}

const ZipCheckStep: React.FC<IZipCheckStepProps> = ({ onSubmit }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const zipEligibilityCheckMutation = useMutation({
    mutationKey: ['check-zip-eligibility'],
    mutationFn: eligibilityApi.checkZipCodeEligibility,
  });
  const { formatMessage } = useIntl();
  const { goToNextStep } = useContext(StepperContext);

  const [zip, setZip] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [isNotIncludedInResponsibleForZIPs, setNotIncludedInResponsibleForZIPs] = useState(false);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value.replace(/\D/g, '');

    if (value.length <= 5) {
      setZip(value);
      setError(null);
    }
  };

  const handleCheck = async () => {
    if (!ZIP_CODE_REGEX.test(zip)) {
      setError(formatMessage({ id: 'eligibilityCheck.step.4.zipCode.invalid' }));
      return;
    }

    const { data } = await zipEligibilityCheckMutation.mutateAsync(zip);

    if (data?.isEligibleByLocalities && data?.isEligibleByResponsibleForZIPs) {
      localStorage.setItem(LocalStorageKey.ZipCode, zip);
      goToNextStep();
      onSubmit();
    } else {
      setNotIncludedInResponsibleForZIPs(true);
    }
  };

  if (isNotIncludedInResponsibleForZIPs) {
    return <NotIncludedInResponsibleForZIPsStep onBack={() => setNotIncludedInResponsibleForZIPs(false)} />;
  }

  return (
    <Stack alignItems="center" gap={4} sx={{ my: theme.spacing(5) }}>
      <Typography variant="h2" textAlign="center">
        <FormattedMessage id="eligibilityCheck.step.4.title" />
      </Typography>
      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 2 : 3}>
        <Input
          placeholder={formatMessage({ id: 'eligibilityCheck.step.4.zipCode.placeholder' })}
          onChange={handleChange}
          value={zip}
          number
          error={!!error}
          helperText={error || ''}
        />
        <Button variant="contained" onClick={handleCheck}>
          <FormattedMessage id="common.check" />
        </Button>
      </Stack>
    </Stack>
  );
};

export default ZipCheckStep;
